import {buildCitationFromEvidence} from './buildCitationFromEvidence';

/**
 * @param {Array} references
 * @param {string} referenceId
 * @returns {Object|null}
 */
export function findReferenceForEvidence(references, referenceId) {
    if (!referenceId || !Array.isArray(references)) return null;
    return references.find(
        (r) => r.localId === referenceId || r.firestoreId === referenceId
    ) || null;
}

/**
 * Stable key for deduplicating the same corpus passage under different referenceIds
 * (e.g. added localId `42:7:0:7:0` vs discovery id `42:7`).
 *
 * @param {Object|null} citation
 * @param {string} referenceId
 * @returns {string}
 */
export function getEvidenceDedupeKey(citation, referenceId) {
    if (citation?.docId != null) {
        const idx = citation.index ?? citation.segmentIndex ?? 0;
        return `doc:${citation.docId}:${idx}`;
    }
    return `ref:${referenceId}`;
}

/**
 * Normalise and dedupe supporting evidence rows for display.
 *
 * @param {Object|null|undefined} claim
 * @param {Array} references Added references for the mark
 * @returns {Array<{
 *   referenceId: string,
 *   reference: Object|null,
 *   corpusEntry: Object|null,
 *   citation: Object|null,
 *   evidence: string|Object,
 * }>}
 */
export function buildSupportingEvidenceRows(claim, references = []) {
    const raw = claim?.supporting_evidence ?? claim?.supportingEvidence ?? [];
    if (!Array.isArray(raw) || raw.length === 0) return [];

    const texts = claim?.supportingEvidenceTexts ?? [];
    const textsByRefId = new Map(
        (Array.isArray(texts) ? texts : []).map((t) => [t.referenceId, t])
    );

    const rowsByKey = new Map();

    for (const evidence of raw) {
        const referenceId = typeof evidence === 'string' ? evidence : evidence?.referenceId;
        if (!referenceId) continue;

        const reference = findReferenceForEvidence(references, referenceId);
        const corpusEntry = reference ? null : (textsByRefId.get(referenceId) ?? null);
        const citation = buildCitationFromEvidence({reference, corpusEntry, referenceId});
        const dedupeKey = getEvidenceDedupeKey(citation, referenceId);

        const row = {
            referenceId,
            reference,
            corpusEntry,
            citation,
            evidence,
        };

        const existing = rowsByKey.get(dedupeKey);
        if (!existing) {
            rowsByKey.set(dedupeKey, row);
            continue;
        }

        // Prefer the row tied to an added reference over a corpus-only discovery id.
        if (!existing.reference && reference) {
            rowsByKey.set(dedupeKey, row);
        }
    }

    return Array.from(rowsByKey.values());
}
